import { ReferralPaymentStatus, UserRole } from "@/types";
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  FormGroup,
  Stack,
  Typography,
} from "@mui/material";
import { FC, useState } from "react";
import { useListContext } from "react-admin";
import { z } from "zod";
import { ReferralUsersListFilter } from "../../types";
import { ReferralUsersFilterProps } from "./referral-users-filter.types";

const initialFormState = {
  roles: [],
  paymentStatuses: [],
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const formSchema = z.object({
  roles: z.array(z.nativeEnum(UserRole)),
  paymentStatuses: z.array(z.nativeEnum(ReferralPaymentStatus)),
});

type FormData = z.infer<typeof formSchema>;

const ReferralUsersFilter: FC<ReferralUsersFilterProps> = ({
  onModalClose,
}) => {
  const [filtersFormData, setFiltersFormData] = useState<Partial<FormData>>({});

  const { filterValues, setFilters } = useListContext();
  const formData: FormData = {
    roles:
      filtersFormData.roles ?? filterValues.roles ?? initialFormState.roles,
    paymentStatuses:
      filtersFormData.paymentStatuses ??
      filterValues.paymentStatuses ??
      initialFormState.paymentStatuses,
  };

  const handleCheckboxChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    field: "roles" | "paymentStatuses"
  ) => {
    const checked = event.target.checked;
    const fieldValue = event.target.name as UserRole | ReferralPaymentStatus;

    let currentArray = formData[field] ? [...formData[field]!] : [];

    if (checked) {
      if (!currentArray.includes(fieldValue)) {
        currentArray.push(fieldValue);
      }
    } else {
      currentArray = currentArray.filter((v) => v !== fieldValue);
    }

    setFiltersFormData({ ...formData, [field]: currentArray });
  };

  const onSubmit = () => {
    const filters: Pick<ReferralUsersListFilter, "roles" | "paymentStatuses"> =
      {
        roles: formData.roles.length ? formData.roles : undefined,
        paymentStatuses: formData.paymentStatuses.length
          ? formData.paymentStatuses
          : undefined,
      };

    setFilters({ ...filterValues, ...filters });
    onModalClose();
  };

  return (
    <Stack spacing={2}>
      <Divider sx={{ borderColor: "#333" }} />

      <FormGroup>
        <Typography variant="h6" align="left">
          Role
        </Typography>
        <FormControlLabel
          name={UserRole.MUSE}
          label={"Woman"}
          labelPlacement="start"
          sx={{ ml: 0, justifyContent: "space-between" }}
          control={
            <Checkbox
              checked={formData.roles.includes(UserRole.MUSE)}
              onChange={(e) => handleCheckboxChange(e, "roles")}
            />
          }
        />
        <FormControlLabel
          name={UserRole.ALPHA}
          label={"Man"}
          labelPlacement="start"
          sx={{ ml: 0, justifyContent: "space-between" }}
          control={
            <Checkbox
              checked={formData.roles.includes(UserRole.ALPHA)}
              onChange={(e) => handleCheckboxChange(e, "roles")}
            />
          }
        />
      </FormGroup>

      <Divider sx={{ borderColor: "#333" }} />

      <FormGroup>
        <Typography variant="h6" align="left">
          Payment Status
        </Typography>
        <FormControlLabel
          name={ReferralPaymentStatus.PAID}
          label={"Paid"}
          labelPlacement="start"
          sx={{ ml: 0, justifyContent: "space-between" }}
          control={
            <Checkbox
              checked={formData.paymentStatuses?.includes(
                ReferralPaymentStatus.PAID
              )}
              onChange={(e) => handleCheckboxChange(e, "paymentStatuses")}
            />
          }
        />
        <FormControlLabel
          name={ReferralPaymentStatus.UNPAID}
          label={"Unpaid"}
          labelPlacement="start"
          sx={{ ml: 0, justifyContent: "space-between" }}
          control={
            <Checkbox
              checked={formData.paymentStatuses?.includes(
                ReferralPaymentStatus.UNPAID
              )}
              onChange={(e) => handleCheckboxChange(e, "paymentStatuses")}
            />
          }
        />
      </FormGroup>

      <Divider sx={{ borderColor: "#333" }} />

      <Box sx={{ mt: 3, display: "flex", justifyContent: "space-between" }}>
        <Button
          variant="outlined"
          onClick={() => {
            setFiltersFormData(initialFormState);
          }}
        >
          Reset
        </Button>

        <Stack direction={"row"} spacing={2}>
          <Button variant="outlined" onClick={onModalClose}>
            Cancel
          </Button>
          <Button variant="contained" color="secondary" onClick={onSubmit}>
            Filter
          </Button>
        </Stack>
      </Box>
    </Stack>
  );
};

export { ReferralUsersFilter };
